import { getMarkRange, Mark } from "@tiptap/core";

/**
 * Toegestane tekstkleuren. De waarde is de class op de <span> in de opgeslagen HTML; de weergave
 * ervan (`.rich-text-editor .green` e.d.) staat in index.css, zodat elke client zelf bepaalt hoe
 * een kleur eruitziet.
 */
export const TEXT_COLORS = ["green", "amber", "red", "brand"] as const;
export type TextColor = (typeof TEXT_COLORS)[number];

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    textColor: {
      setTextColor: (color: TextColor) => ReturnType;
      unsetTextColor: () => ReturnType;
    };
  }
}

/**
 * Vervangt Color/TextStyle uit @tiptap/extension-text-style: die schrijven een inline `style`,
 * deze schrijft alleen een class uit TEXT_COLORS. Spans met een andere (of geen) class worden bij
 * het inlezen genegeerd, dus willekeurige classes of inline kleuren komen niet in de editor terecht.
 */
export const TextColorExtension = Mark.create({
  name: "textColor",

  addAttributes() {
    return {
      color: {
        default: null,
        renderHTML: (attributes) => (attributes.color ? { class: attributes.color } : {}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span",
        getAttrs: (element) => {
          if (typeof element === "string") return false;
          const color = TEXT_COLORS.find((candidate) => element.classList.contains(candidate));
          return color ? { color } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      // Een mark sluit zichzelf uit, dus een nieuwe kleur vervangt de bestaande.
      setTextColor:
        (color) =>
        ({ state, tr, dispatch, commands }) => {
          const type = state.schema.marks[this.name];
          // Zonder selectie zou setMark alleen de kleur van de volgende tekst wijzigen, terwijl de
          // werkbalk de kleur van de tekst onder de cursor toont. Daarom de hele gekleurde tekst.
          const range = state.selection.empty
            ? getMarkRange(state.selection.$from, type)
            : undefined;
          if (!range) return commands.setMark(this.name, { color });
          if (dispatch) tr.addMark(range.from, range.to, type.create({ color }));
          return true;
        },
      // Idem: met alleen een cursor in gekleurde tekst moet de hele gekleurde tekst ontkleurd worden.
      unsetTextColor:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name, { extendEmptyMarkRange: true }),
    };
  },
});
